import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Badge,
  Chip,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import Avatar from '../../components/Avatar';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
// hooks
import { POSITION_OPTION_DICT } from '../../constants';


const PrimaryText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.text.primary,
  };
});

const DisabledText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.text.disabled,
  };
});

const OverlayContent = styled(CardContent)(({ theme }) => ({
  zIndex: 999,
  width: '100%',
  justifyContent: 'center',
  justifyItems: 'center',
  alignItems: 'center',
  position: 'absolute',
  m: 0,
  p: 0,
}))

// ----------------------------------------------------------------------

export default function FliersProfileCard({ profile, selectProfile=null, hasBorder=false, showDetail=true }) {
  const [chipCount, setChipCount] = useState(0);
  const [chipShowCount, setChipShowCount] = useState(0);

  const chipListId = `fliers-chip-list-${profile?.id || '0'}`;
  const firstChipId = `fliers-first-chip-${profile?.id || '0'}`;
  const showGroup = profile?.position === 'developer';
  const hasOverlay = showDetail && profile?.coffee_chat_ing;

  const getTextWidth = (text) => {
    // 추정치
    const paddingLength = 4;
    const charWidth = 11.3;
    const spaceWidth = 3.3;
    const charLength = text?.length || 0;
    const spaceCount = text.match(/([\s]+)/g) ? text.match(/([\s]+)/g).length : 0;
    const charCount = charLength - spaceCount;
    return charCount * charWidth + spaceCount * spaceWidth + paddingLength;
  }

  const calculateChipOverflowCount = () => {
    if (!profile?.id) return 0;
    if (!document.getElementById(firstChipId)) return 0;

    const totalCount = (profile?.matching_roles?.length || 0) + (profile?.main_role?.short_name ? 1 : 0);

    const firstMarkLength = 18;
    const spaceLength = 8;
    const limitLength = document.getElementById(chipListId).getBoundingClientRect().width - 86;
    let showCount = 0;
    let showLength = 0;

    if (profile?.main_role?.short_name) {
      const roleLength = getTextWidth(profile?.main_role?.short_name);
      if (roleLength < limitLength) {
        showCount += 1;
        showLength += roleLength;
        showLength += spaceLength;
        showLength += firstMarkLength;
      }
    }

    profile?.matching_roles?.forEach((role, index) => {
      if ((showLength + getTextWidth(role?.short_name) < limitLength) || ((index + 2) === totalCount && (showCount + 1) === totalCount && showLength + getTextWidth(role?.short_name) < limitLength + 60)) {
        showCount += 1;
        showLength += getTextWidth(role?.short_name);
        showLength += spaceLength;
      }
    });
    setChipShowCount(showCount);
  }
  
  useEffect(() => {
    const totalCount = (profile?.matching_roles?.length || 0) + (profile?.main_role?.short_name ? 1 : 0);
    setChipCount(totalCount);
    setChipShowCount(totalCount);
    calculateChipOverflowCount();
    window.addEventListener('resize', () => {
      calculateChipOverflowCount();
    });
    return () => {
      window.removeEventListener('resize', () => {
        calculateChipOverflowCount();
      });
    }
  }, []);

  return (
    <Card
      sx={{
        ...(selectProfile && {
          '&:hover': {
            cursor: 'pointer',
            boxShadow: (theme) => theme.customShadows.z24,
          },
        }),
        ...(hasBorder && { border: (theme) => `solid 1px ${theme.palette.secondary.main}` }),
      }}
      onClick={() => selectProfile && selectProfile(profile)}
    >
      {hasOverlay && (
        <OverlayContent
          sx={{
            p:0, m:0, textAlign: 'center', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
            '&:hover': {
              cursor: 'pointer',
            }
          }}
        >
          <Label
            variant="outlined"
            color="info"
            size="large"
            sx={{
              height: 30,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            커피챗 제안 완료
          </Label>
        </OverlayContent>
      )}
      <CardContent sx={{ py: 2, '&:last-child': { py: 2 } }}>
        <Stack
          direction="column"
          spacing={1.5}
        >
          <Stack
            direction={"row-reverse"} spacing={3}
            sx={{
              width: 1,
            }}
            justifyContent={"space-between"}
          >
            <Badge
              sx={{
                width: 48,
                height: 48,
              }}
              invisible={!profile?.has_save_list && !profile?.has_view_history}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                profile?.has_save_list ? (
                  <Iconify icon={'eva:heart-fill'} sx={{ color: "error.main", mr: 2, mb: 3, width: 20, height: 20 }} />
                ) : (
                  <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "info.main", mr: 2, mb: 3, width: 20, height: 20 }} />
                )
              }
            >
              <Avatar
                src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${profile?.crew_hash || '5'}.png`}
                alt={profile?.name}
                color={'default'}
                sx={{
                  margin: 'auto',
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: 'common.white',
                  width: 48,
                  height: 48,
                }}
              />
            </Badge>
            <Stack spacing={1} sx={{ overflow: "hidden" }}>
              <Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6">
                    {showGroup ? profile?.main_role?.group : POSITION_OPTION_DICT[profile?.position]}
                  </Typography>
                  {/* 글자 줄넘김 방지 */}
                  {showGroup && (
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: '42px' }}>
                    {POSITION_OPTION_DICT[profile?.position]}
                  </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" divider={<Divider orientation={"vertical"} flexItem />}>
                  <Typography variant="body2" color="text.secondary" sx={{ textOverflow: "ellipsis" }} noWrap>
                    <PrimaryText>{profile?.name}</PrimaryText> 크루
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textOverflow: "unset", minWidth: "38px" }} noWrap>
                    {profile?.years}년차
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" sx={{ width: "100%", opacity: hasOverlay ? 0.3 : 1 }} alignItems="center" id={chipListId} spacing={.5}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: "hidden" }}>
              <Chip
                id={firstChipId}
                icon={<Iconify icon={'ic:outline-check'} />}
                label={profile?.main_role?.short_name}
                size="small"
                sx={{ backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.16), color: "primary.main" }}
              />
              {profile?.matching_roles && profile?.matching_roles.map((role, index) => {
                if ((index + 1) >= chipShowCount) return null;
                return (
                  <Chip
                    id={`fliers-role-chip-${profile?.id}-${index}`}
                    key={index}
                    label={role.short_name}
                    size="small"
                    sx={{ backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.16), color: "primary.main" }}
                  />
                )
              })}
            </Stack>
            {chipCount - chipShowCount > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: "70px", wordBreak: "break-all" }}
              >
                외 {chipCount - chipShowCount}개 스택
              </Typography>
            )}
          </Stack>
          {showDetail && profile?.career_name && (
            <>
            <Divider orientation="horizontal" flexItem />
            <Stack sx={{ width: 1, opacity: hasOverlay ? 0.3 : 1 }} spacing={0.5}>
              {profile?.career_name && (
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ color: "text.disabled", width: 16, height: 16, minWidth: 16 }} />
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  {profile?.career_name}
                </Typography>
              </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ visibility: 'hidden', color: "text.disabled", width: 16, height: 16, minWidth: 16 }} />
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  {profile?.second_career_name || ''}{profile?.career_count && profile?.career_count > 2 && (<DisabledText> 외 {profile?.career_count - 2}개 이력</DisabledText>)}
                </Typography>
              </Stack>
            </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
